import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ProjectInfo } from '../api/types';
import { ProjectExtra } from '../data/projectExtras';
import { useLanguage } from '../i18n/LanguageContext';
import HoodDiagram from './HoodDiagram';

interface ProjectCardProps {
  project: ProjectInfo;
  extra?: ProjectExtra;
  num: string;
}

export function displayDomain(url: string) {
  return url.replace(/^https?:\/\//, '').replace(/\/$/, '');
}

function ProjectCard({ project, extra, num }: ProjectCardProps) {
  const { t, lang } = useLanguage();
  const [view, setView] = useState<'product' | 'hood'>('product');

  const title = extra?.title ?? (lang === 'en' ? project.title_en : project.title_tr || project.title_en);
  const desc = extra?.desc[lang] ?? (lang === 'en' ? project.description_en : project.description_tr || project.description_en);
  const [img, img2] = project.image_urls;
  const domain = displayDomain(project.link_url);
  const hasHood = !!extra?.diagram;

  return (
    <article className="project-card rv-scale">
      <div className="project-info">
        <div className="project-num-row">
          <span className="project-num rv-num">{num}</span>
          <span className="project-kind">{extra?.kind[lang] ?? t.work.project}</span>
        </div>
        <h3 className="project-title rv">{title}</h3>
        <p className={`project-desc rv rv-1${extra ? '' : ' clamp'}`}>{desc}</p>
        {extra?.role[lang] && (
          <div className="project-meta rv rv-2">
            <div>
              <span className="project-meta-key">{t.work.role}</span>
              <span>{extra.role[lang]}</span>
            </div>
            <div>
              <span className="project-meta-key">Stack</span>
              <span className="project-meta-dim">{extra.stack}</span>
            </div>
          </div>
        )}
        <div className={`project-actions rv rv-3${extra?.caseSlug ? '' : ' only-live'}`}>
          {extra?.caseSlug && (
            <Link to={`/blog/${extra.caseSlug}`} className="btn btn-solid btn-sm">
              {t.work.caseStudy}
            </Link>
          )}
          <a href={project.link_url} target="_blank" rel="noreferrer" className="btn btn-line btn-sm project-live">
            {t.work.live}
          </a>
        </div>
      </div>

      <div className="project-visual">
        <div className="project-visual-bar">
          {hasHood ? (
            <div className="ft-seg project-toggle">
              <button type="button" aria-pressed={view === 'product'} className={view === 'product' ? 'active' : ''} onClick={() => setView('product')}>
                {t.work.product}
              </button>
              <button type="button" aria-pressed={view === 'hood'} className={view === 'hood' ? 'active-acc' : ''} onClick={() => setView('hood')}>
                {t.work.hood}
              </button>
            </div>
          ) : (
            <span />
          )}
          <a href={project.link_url} target="_blank" rel="noreferrer" className="project-domain">
            {domain} ↗
          </a>
        </div>

        {view === 'product' || !extra?.diagram ? (
          <a
            href={project.link_url}
            target="_blank"
            rel="noreferrer"
            aria-label={`${title} — ${t.work.live}`}
            className={`project-shots${img2 ? ' has-second' : ''}`}
          >
            <div className="browser-frame">
              <div className="browser-bar">
                <span className="browser-dot" />
                <span className="browser-dot" />
                <span className="browser-dot" />
                <span className="browser-url">{extra?.frame1 || domain}</span>
              </div>
              {img && <img src={img} alt={title} loading="lazy" />}
            </div>
            {img2 && (
              <div className="browser-frame browser-frame-float">
                <div className="browser-bar-mini">{extra?.frame2 || domain}</div>
                <img src={img2} alt={title} loading="lazy" />
              </div>
            )}
          </a>
        ) : (
          <HoodDiagram diagram={extra.diagram} diagramTr={extra.diagramTr} />
        )}
      </div>
    </article>
  );
}

export default ProjectCard;
